const HEX_PATTERN = /^#([0-9a-fA-F]{6})$/;

export function isValidHexColor(value: string): boolean {
  return HEX_PATTERN.test(value);
}

function parseHex(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '');
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function toHex(r: number, g: number, b: number): string {
  const clamp = (channel: number): number => Math.max(0, Math.min(255, Math.round(channel)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`;
}

function mix(hex: string, target: { r: number; g: number; b: number }, weight: number): string {
  const source = parseHex(hex);
  const w = Math.max(0, Math.min(1, weight));
  return toHex(
    source.r + (target.r - source.r) * w,
    source.g + (target.g - source.g) * w,
    source.b + (target.b - source.b) * w,
  );
}

/* eslint-disable no-restricted-syntax -- color-scale utility works with hex */
/** Generates Tailwind-compatible brand steps from a single primary hex. */
export function generateBrandScale(primaryHex: string): Record<string, string> {
  const primary = isValidHexColor(primaryHex) ? primaryHex : '#0097a7';
  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 15, g: 26, b: 28 };

  return {
    '50': mix(primary, white, 0.92),
    '100': mix(primary, white, 0.84),
    '200': mix(primary, white, 0.68),
    '300': mix(primary, white, 0.5),
    '400': mix(primary, white, 0.28),
    '500': primary,
    '600': mix(primary, black, 0.18),
    '700': mix(primary, black, 0.32),
    '800': mix(primary, black, 0.44),
    '900': mix(primary, black, 0.56),
  };
}

export function normalizeHexColor(value: string | undefined, fallback: string): string {
  if (value && isValidHexColor(value)) {
    return value.toLowerCase();
  }
  return fallback;
}
